import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Swiper } from 'antd-mobile';
import * as echarts from 'echarts';

const indicatorFullNames = [
    'Energy Efficiency',
    'Equipment Condition',
    'Occupant Comfort',
    'Service Efficiency',
    'Technology Adoption',
    'Cost Efficiency'
];

const getAbbreviation = (name) => {
    return name.split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .join('');
};

const metricIndicators = indicatorFullNames.map((fullName) => ({
    name: getAbbreviation(fullName),
    fullName,
    max: 10
}));

const formatScore = (score) => {
    if (score === null || score === undefined || Number.isNaN(Number(score))) {
        return '-';
    }

    return Number(score).toFixed(2);
};

const SpiderChart = ({ 
    radarData = [], 
    facilities = [], // 新增 facilities 参数来获取颜色信息
    title = "Portfolio Performance Overview", 
    showTitle = false,
    showOverallScore = false,
    overallScore = null,
    useMobileDrawerTooltip = false,
    mobileCarousel = false,
    style = { width: '100%', height: '400px' } 
}) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const swiperRef = useRef(null);
    const [activeSlide, setActiveSlide] = useState(0);
    const hasOverallScore = overallScore !== null && overallScore !== undefined && !Number.isNaN(Number(overallScore));
    const drawerMetrics = useMemo(() => {
        const values = radarData[0]?.radarData || [];
        return metricIndicators.map((indicator, index) => ({
            ...indicator,
            value: values[index]
        }));
    }, [radarData]);

    // 初始化雷达图
    const initRadarChart = (retryCount = 0) => {
        const chartDom = chartRef.current;
        if (!chartDom) {
            if (retryCount < 5) {
                setTimeout(() => initRadarChart(retryCount + 1), 500);
            }
            return;
        }

        if (!radarData.length) {
            console.log('No radar data');
            return;
        }
        
        // 检查容器尺寸
        if (chartDom.offsetWidth === 0 || chartDom.offsetHeight === 0) {
            setTimeout(() => initRadarChart(), 500);
            return;
        }
        
        // 清除之前的图表实例
        if (chartInstanceRef.current) {
            chartInstanceRef.current.dispose();
        }
        
        const myChart = echarts.init(chartDom);
        chartInstanceRef.current = myChart;
        
        // 创建 facility 颜色映射
        const facilityColorMap = {};
        facilities.forEach(facility => {
            facilityColorMap[facility.id] = facility.color;
        });
        
        // 创建名称到缩写的映射函数
        const getAbbreviation = (name) => {
            // 将名称按空格分割，取每个单词的首字母并转为大写
            return name.split(' ')
                .map(word => word.charAt(0).toUpperCase())
                .join('');
        };
        
        // 定义指标全称列表
        const indicatorFullNames = [
            'Energy Efficiency',
            'Equipment Condition',
            'Occupant Comfort',
            'Service Efficiency',
            'Technology Adoption',
            'Cost Efficiency'
        ];
        
        // 创建名称映射表，用于 tooltip 显示全称
        const nameMap = {};
        const indicators = indicatorFullNames.map((fullName) => {
            const abbrev = getAbbreviation(fullName);
            nameMap[abbrev] = fullName;
            return {
                name: abbrev, // 使用缩写作为显示名称
                max: 10
            };
        });
        
        const mobileFacilityScore = !!useMobileDrawerTooltip;
        const titleText = showOverallScore && hasOverallScore ? `${title}  ${formatScore(overallScore)}` : title;

        const option = {
            backgroundColor: '#fff',
            title: showTitle ? {
                text: titleText,
                left: 'center',
                top: mobileCarousel ? '3%' : '2%',
                textStyle: {
                    color: '#396598',
                    fontSize: mobileCarousel ? 13 : 14,
                    fontWeight: 600
                }
            } : undefined,
            tooltip: mobileFacilityScore ? { show: false } : {
                trigger: 'item',
                confine: false, // 允许tooltip超出canvas边界
                enterable: true, // 允许鼠标进入tooltip
                appendToBody: true, // 将tooltip添加到body，避免被容器限制
                extraCssText: 'z-index: 9999;', // 设置更高的z-index
                className: 'custom-tooltip',
                formatter: function(params) {
                    // 对于雷达图，params.value 是一个数组，包含所有指标的值
                    if (Array.isArray(params.value)) {
                        // params.value 是值数组，需要与 indicators 对应
                        return params.value.map((value, index) => {
                            // 通过索引找到对应的指标名称
                            const indicatorName = indicators[index]?.name || '';
                            const fullName = nameMap[indicatorName] || indicatorFullNames[index] || indicatorName;
                            // 显示格式：全称(缩写): 值
                            return `${fullName}(${indicatorName}): ${value}`;
                        }).join('<br/>');
                    } else if (Array.isArray(params)) {
                        // 如果是参数数组
                        return params.map(param => {
                            const fullName = nameMap[param.name] || param.name;
                            return `${fullName}(${param.name}): ${param.value}`;
                        }).join('<br/>');
                    } else {
                        // 单个数据点
                        const fullName = nameMap[params.name] || params.name;
                        return `${fullName}(${params.name}): ${params.value}`;
                    }
                }
            },
            legend: {
                show: false
            },
            radar: {
                indicator: indicators,
                center: mobileFacilityScore ? ['50%', '54%'] : (showTitle ? ['50%', '58%'] : ['50%', '50%']),
                radius: mobileFacilityScore ? '55%' : (showTitle ? '62%' : '70%'),
                axisName: {
                    color: '#666',
                    fontSize: mobileFacilityScore ? 11 : 12,
                    padding: mobileFacilityScore ? [-2, -2, -2, -2] : 0,
                    formatter: function(value) {
                        // 直接返回缩写，因为已经转换过了
                        return value;
                    }
                },
                splitLine: {
                    lineStyle: {
                        color: '#e6e6e6'
                    }
                },
                axisLine: {
                    lineStyle: {
                        color: '#e6e6e6'
                    }
                }
            },
            series: [{
                type: 'radar',
                data: radarData.map((item, index) => ({
                    value: item.radarData,
                    name: item.facilityName,
                    itemStyle: {
                        // 使用 facility 的颜色，如果没有则使用默认颜色
                        color: facilityColorMap[item.facilityId] || `hsl(${index * 360 / radarData.length}, 70%, 50%)`
                    },
                    lineStyle: {
                        color: facilityColorMap[item.facilityId] || `hsl(${index * 360 / radarData.length}, 70%, 50%)`
                    },
                    areaStyle: {
                        opacity: 0 // 设置为完全透明，不显示填充
                    }
                }))
            }]
        };
        
        myChart.setOption(option);

        if (mobileFacilityScore) {
            myChart.off('click');
        } else {
            myChart.off('click');
        }
        
        console.log('Radar chart option set successfully');
        
        // 响应式处理
        const resizeHandler = () => {
            if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
                chartInstanceRef.current.resize();
            }
        };
        
        window.addEventListener('resize', resizeHandler);
        
        // 强制重绘
        setTimeout(() => {
            if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
                chartInstanceRef.current.resize();
            }
        }, 100);
        
        // 清理函数
        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    };

    useEffect(() => {
        // 使用 requestAnimationFrame 确保DOM完全渲染
        let cleanupRadarChart;
        let cancelled = false;
        const timer = setTimeout(() => {
            requestAnimationFrame(() => {
                if (!cancelled) {
                    cleanupRadarChart = initRadarChart();
                }
            });
        }, 100);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            if (typeof cleanupRadarChart === 'function') {
                cleanupRadarChart();
            }
            if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
                chartInstanceRef.current.dispose();
            }
        };
    }, [radarData, facilities, title, overallScore, showOverallScore, showTitle, useMobileDrawerTooltip, mobileCarousel]);

    const renderDetailCard = () => (
        <div className="facility-score-detail-card">
            <div className="facility-score-detail-header">
                <div>
                    <div className="facility-score-detail-kicker">Score Breakdown</div>
                </div>
                <div className="facility-score-detail-total">
                    {formatScore(overallScore)}
                </div>
            </div>
            <div className="facility-score-detail-grid">
                {drawerMetrics.map(metric => {
                    const value = Number(metric.value || 0);
                    const width = Math.max(0, Math.min(100, value * 10));

                    return (
                        <div className="facility-score-detail-tile" key={metric.name}>
                            <div className="facility-score-detail-tile-top">
                                <span>{metric.name}</span>
                                <strong>{metric.value ?? '-'}</strong>
                            </div>
                            <div className="facility-score-detail-name">
                                {metric.fullName}
                            </div>
                            <div className="facility-score-detail-track">
                                <div
                                    className="facility-score-detail-bar"
                                    style={{ width: `${width}%` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const handleSlideTo = (index) => {
        setActiveSlide(index);
        if (swiperRef.current) {
            swiperRef.current.swipeTo(index);
        }
    };

    return (
        <>
            {mobileCarousel ? (
                <div className="facility-score-carousel" style={style}>
                    <Swiper
                        ref={swiperRef}
                        loop={false}
                        indicator={false}
                        onIndexChange={setActiveSlide}
                    >
                        <Swiper.Item>
                            <div className="facility-score-carousel-slide">
                                <div ref={chartRef} className="facility-score-chart-canvas" />
                            </div>
                        </Swiper.Item>
                        <Swiper.Item>
                            <div className="facility-score-carousel-slide">
                                {renderDetailCard()}
                            </div>
                        </Swiper.Item>
                    </Swiper>
                    <div className="facility-score-carousel-header">
                        <div className="facility-score-carousel-dots" aria-label="Score carousel indicator">
                            {[0, 1].map(index => (
                                <button
                                    key={index}
                                    type="button"
                                    className={`facility-score-carousel-dot${index === activeSlide ? " active" : ""}`}
                                    aria-label={index === 0 ? "Show score chart" : "Show score detail"}
                                    aria-current={index === activeSlide ? "true" : undefined}
                                    onClick={() => handleSlideTo(index)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div 
                    ref={chartRef}
                    style={style}
                />
            )}
            {mobileCarousel ? (
                <style>
                    {`
                        .facility-score-carousel,
                        .facility-score-carousel .adm-swiper,
                        .facility-score-carousel .adm-swiper-track,
                        .facility-score-carousel .adm-swiper-slide,
                        .facility-score-carousel-slide {
                            height: 100%;
                        }

                        .facility-score-carousel {
                            position: relative;
                            background: #fff;
                            touch-action: pan-y;
                        }

                        .facility-score-carousel-header {
                            position: absolute;
                            left: 0;
                            right: 0;
                            bottom: 8px;
                            z-index: 10;
                            display: flex;
                            justify-content: center;
                        }

                        .facility-score-carousel-dots {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 6px;
                            padding: 4px 6px;
                            border-radius: 10px;
                            background: rgba(255, 255, 255, 0.82);
                            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
                        }

                        .facility-score-carousel-dot {
                            width: 7px;
                            height: 7px;
                            padding: 0;
                            border: none;
                            border-radius: 50%;
                            background: #d8dee9;
                            cursor: pointer;
                            transition: width 0.3s, border-radius 0.3s, background 0.3s;
                        }

                        .facility-score-carousel-dot.active {
                            width: 18px;
                            border-radius: 999px;
                            background: #1677ff;
                        }

                        .facility-score-carousel-dot:focus-visible {
                            outline: 2px solid rgba(22, 119, 255, 0.35);
                            outline-offset: 2px;
                        }

                        .facility-score-chart-canvas {
                            width: 100%;
                            height: 100%;
                        }

                        .facility-score-detail-card {
                            height: calc(100% - 34px);
                            margin: 8px 9px 26px;
                            padding: 10px 11px;
                            border: 1px solid #e7eef8;
                            border-radius: 8px;
                            background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
                            box-shadow: 0 6px 18px rgba(22, 48, 89, 0.08);
                            overflow: hidden;
                            display: flex;
                            flex-direction: column;
                        }

                        .facility-score-detail-header {
                            display: flex;
                            align-items: flex-start;
                            justify-content: space-between;
                            gap: 12px;
                            margin-bottom: 7px;
                            flex: 0 0 auto;
                        }

                        .facility-score-detail-kicker {
                            color: #7a8aa0;
                            font-size: 10px;
                            font-weight: 600;
                            text-transform: uppercase;
                            letter-spacing: 0;
                        }

                        .facility-score-detail-title {
                            color: #203756;
                            font-size: 14px;
                            font-weight: 700;
                            line-height: 1.25;
                            margin-top: 3px;
                        }

                        .facility-score-detail-total {
                            min-width: 54px;
                            padding: 5px 8px;
                            border-radius: 8px;
                            color: #fff;
                            background: #396598;
                            font-size: 16px;
                            font-weight: 800;
                            text-align: center;
                            line-height: 1;
                        }

                        .facility-score-detail-grid {
                            flex: 1 1 auto;
                            min-height: 0;
                            display: grid;
                            grid-template-columns: repeat(2, minmax(0, 1fr));
                            grid-template-rows: repeat(3, minmax(0, 1fr));
                            gap: 6px;
                        }

                        .facility-score-detail-tile {
                            min-width: 0;
                            min-height: 0;
                            padding: 6px 7px;
                            border: 1px solid #eef2f7;
                            border-radius: 8px;
                            background: rgba(255, 255, 255, 0.84);
                            display: flex;
                            flex-direction: column;
                            justify-content: space-between;
                        }

                        .facility-score-detail-tile-top {
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            gap: 6px;
                            color: #23364f;
                            font-size: 11px;
                            font-weight: 800;
                            line-height: 1;
                        }

                        .facility-score-detail-tile-top strong {
                            color: #111827;
                            font-size: 13px;
                        }

                        .facility-score-detail-name {
                            color: #637187;
                            font-size: 9px;
                            line-height: 1.15;
                            margin: 4px 0 5px;
                            overflow: hidden;
                            display: -webkit-box;
                            -webkit-line-clamp: 2;
                            -webkit-box-orient: vertical;
                        }

                        .facility-score-detail-track {
                            height: 4px;
                            border-radius: 999px;
                            background: #e8edf5;
                            overflow: hidden;
                        }

                        .facility-score-detail-bar {
                            height: 100%;
                            border-radius: inherit;
                            background: linear-gradient(90deg, #2f80ed 0%, #20b486 100%);
                        }
                    `}
                </style>
            ) : null}
        </>
    );
};

export default React.memo(SpiderChart);

